<template>
  <div class="panel-organization-configuration-search flex flex-col gap-4">
    <panel-organization-configuration-item title="Recherche Typesense">
      <div class="panel-organization-configuration-search__content flex flex-col gap-4 w-full">
        <BcButton @click="generateTypesenseCredentials" :disabled="isTypesenseCredentialsGenerated">Générer les identifiants Typesense</BcButton>
        <BcButton @click="generateTypesenseCredentials" :disabled="!isTypesenseCredentialsGenerated">Re-générer les identifiants Typesense</BcButton>
        <div class="flex items-center gap-2 w-full justify-between">
          <BcButton @click="syncTypesenseCoders" :disabled="!isTypesenseCredentialsGenerated">Synchroniser les coders Typesense</BcButton>
          <p class="px-2 py-1 bg-blue-200 rounded-md"><strong>{{ codersCollection?.num_documents || 0 }}</strong> candidats synchronisés</p>
        </div>
        <div class="flex items-center gap-2 w-full justify-between">
          <BcButton @click="syncTypesenseCompanies" :disabled="!isTypesenseCredentialsGenerated">Synchroniser les companies Typesense</BcButton>
          <p class="px-2 py-1 bg-blue-200 rounded-md"><strong>{{ companiesCollection?.num_documents || 0 }}</strong> entreprises synchronisées</p>
        </div>
        <div class="flex items-center gap-2 w-full justify-between">
          <BcButton @click="syncTypesenseContacts" :disabled="!isTypesenseCredentialsGenerated">Synchroniser les contacts Typesense</BcButton>
          <p class="px-2 py-1 bg-blue-200 rounded-md"><strong>{{ contactsCollection?.num_documents || 0 }}</strong> contacts synchronisés</p>
        </div>
      </div>
    </panel-organization-configuration-item>
    <panel-organization-configuration-item title="Recherche Algolia">
      <div class="panel-organization-configuration-search__content flex flex-col gap-4">
        <BcButton :disabled="isAlgoliaCredentialsGenerated" @click="generateTypesenseCredentials">Générer les identifiants Algolia</BcButton>
        <BcButton :disabled="!isAlgoliaCredentialsGenerated" @click="generateTypesenseCredentials">Re-générer les identifiants Algolia</BcButton>
        <BcButton :disabled="!isAlgoliaCredentialsGenerated" @click="syncTypesenseCoders">Synchroniser les coders Algolia</BcButton>
      </div>
    </panel-organization-configuration-item>
  </div>
</template>

<script setup lang="ts">
import PanelOrganizationConfigurationItem from './PanelOrganizationConfigurationItem.vue';
import BcButton from '@/ui-kit/components/BcButton/BcButton.vue';
import { generateTypesenseCredentials as generateTypesenseCredentialsApi, syncTypesenseCoders as syncTypesenseCodersApi, syncTypesenseCompanies as syncTypesenseCompaniesApi, syncTypesenseContacts as syncTypesenseContactsApi } from '@/api/organizations';
import { useToast } from '@/ui-kit/components/BcToast';
import { ref, watch } from 'vue';
import axios from 'axios';

const props = defineProps({
  profile: {
    type: Object,
    required: true,
  },
});

const toast = useToast();
const isTypesenseCredentialsGenerated = ref(props.profile.configuration.integrations.typesense?.credentials?.appKey || false);
const isAlgoliaCredentialsGenerated = ref(props.profile.configuration.integrations.algolia?.credentials?.appKey || false);

const codersCollection = ref(null);
const companiesCollection = ref(null);
const contactsCollection = ref(null);
async function syncTypesenseCoders() {
  try {
    await syncTypesenseCodersApi(props.profile._id);
    toast.show({
      type: 'info',
      message: 'Synchronisation des candidats en cours',
    });
  } catch (err) {
    toast.show({
      type: 'error',
      message: err?.message,
    });
  }
}

async function syncTypesenseCompanies() {
  try {
    await syncTypesenseCompaniesApi(props.profile._id);
    toast.show({
      type: 'success',
      message: 'Synchronisation des entreprises en cours',
    });
  } catch (err) {
    toast.show({
      type: 'error',
      message: err?.message,
    });
  }
}

async function syncTypesenseContacts() {
  try {
    await syncTypesenseContactsApi(props.profile._id);
    toast.show({
      type: 'success',
      message: 'Synchronisation des contacts en cours',
    });
  } catch (err) {
    toast.show({
      type: 'error',
      message: err?.message,
    });
  }
}
async function generateTypesenseCredentials() {
  try {
    await generateTypesenseCredentialsApi(props.profile._id);
    isTypesenseCredentialsGenerated.value = true;
    toast.show({
      type: 'success',
      message: 'Génération des identifiants Typesense réussie',
    });
  } catch (err) {
    toast.show({
      type: 'error',
      message: err?.message,
    });
  }
}

async function getCodersCollection() {
  try {
    const { data } = await axios.get(`${import.meta.env.VITE_TYPESENSE_URL || 'http://localhost:8108'}/collections/coders_${props.profile._id}_${import.meta.env.VITE_TYPESENSE_ENV}`, {
      headers: {
        'X-TYPESENSE-API-KEY': import.meta.env.VITE_TYPESENSE_API_KEY || 'xyz',
      },
    });
    codersCollection.value = data;
  } catch (err) {
    if (err?.response?.status !== 404) {
      toast.show({
        type: 'error',
        message: err?.response?.data?.message || 'Une erreur est survenue lors de la récupération de la collection des coders',
      });
    }
  }
}

async function getCompaniesCollection() {
  try {
    const { data } = await axios.get(`${import.meta.env.VITE_TYPESENSE_URL || 'http://localhost:8108'}/collections/companies_${props.profile._id}_${import.meta.env.VITE_TYPESENSE_ENV}`, {
      headers: {
        'X-TYPESENSE-API-KEY': import.meta.env.VITE_TYPESENSE_API_KEY || 'xyz',
      },
    });
    companiesCollection.value = data;
  } catch (err) {
    if (err?.response?.status !== 404) {
      toast.show({
        type: 'error',
        message: err?.response?.data?.message || 'Une erreur est survenue lors de la récupération de la collection des entreprises',
      });
    }
  }
}

async function getContactsCollection() {
  try {
    const { data } = await axios.get(`${import.meta.env.VITE_TYPESENSE_URL || 'http://localhost:8108'}/collections/contacts_${props.profile._id}_${import.meta.env.VITE_TYPESENSE_ENV}`, {
      headers: {
        'X-TYPESENSE-API-KEY': import.meta.env.VITE_TYPESENSE_API_KEY || 'xyz',
      },
    });
    contactsCollection.value = data;
  } catch (err) {
    if (err?.response?.status !== 404) {
      toast.show({
        type: 'error',
        message: err?.response?.data?.message || 'Une erreur est survenue lors de la récupération de la collection des contacts',
      });
    }
  }
}

watch(props?.profile?._id, async () => {
  await getCodersCollection();
  await getCompaniesCollection();
  await getContactsCollection();
}, { immediate: true });
</script>

<style lang="scss" scoped>
.panel-organization-configuration-features {
  &__content {
    flex-direction: column;
    width: 100%;
    gap: 16px;
  }

  &__item {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
